import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Button from '../ui/Button';
import { useScrollLock } from '../../hooks/useScrollLock';
import { useContactForm } from '../../contexts/ContactFormContext';

const UnifiedHeader: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { lockScroll, unlockScroll } = useScrollLock();
  const navigate = useNavigate();
  const location = useLocation();
  const { openForm: openContactForm } = useContactForm();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle menu open/close
  useEffect(() => {
    if (isMenuOpen) {
      lockScroll();
      menuRef.current?.focus();
    } else {
      unlockScroll();
    }
  }, [isMenuOpen, lockScroll, unlockScroll]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isMenuOpen]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  // Unified navigation handler
  const handleNavigation = (targetPath: string, targetHash?: string) => {
    closeMenu();
    
    if (targetHash) {
      // Navigation with hash (e.g., to home page sections)
      if (location.pathname === '/') {
        // Already on home page, just scroll
        setTimeout(() => {
          const element = document.getElementById(targetHash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 0);
      } else {
        // Navigate to home page with hash
        navigate(`/#${targetHash}`);
      }
    } else {
      // Regular navigation (e.g., to /blog)
      navigate(targetPath);
    }
  };

  const navLinks = [
    { path: '/', hash: 'services', label: 'Services', ariaLabel: 'View our services' },
    { path: '/', hash: 'portfolio', label: 'Work', ariaLabel: 'View our portfolio' },
    { path: '/', hash: 'pricing', label: 'Pricing', ariaLabel: 'View pricing plans' },
    { path: '/blog', hash: undefined, label: 'Blog', ariaLabel: 'Read our blog' },
    { path: '/', hash: 'about', label: 'About', ariaLabel: 'Learn about us' },
  ];

  return (
    <>
      <header 
        className={`fixed top-0 z-50 w-full transition-all duration-300 ${
          isScrolled ? 'bg-background/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'
        }`}
        role="banner"
      >
        <div className="container mx-auto px-3 sm:px-4 lg:px-6 flex h-20 items-center justify-between">
          <div className="flex items-center">
            <button 
              onClick={(e) => {
                e.preventDefault();
                console.log('Logo clicked from:', location.pathname);
                if (location.pathname === '/') {
                  // If on home page, smooth scroll to top
                  console.log('Scrolling to top');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                  // If on other page, navigate to home page
                  console.log('Navigating to home');
                  navigate('/');
                }
              }}
              className="focus-ring rounded-sm"
              aria-label="Paokhum homepage"
            >
              <img 
                src="/Paokhum_logo_dark.svg" 
                alt="Paokhum" 
                width="241"
                height="24"
                className="h-6 w-auto"
              />
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav 
            className="hidden lg:flex items-center space-x-8 text-sm font-mono uppercase tracking-widest"
            aria-label="Main navigation"
          >
            {navLinks.map(link => {
              // Use hash navigation for section links, regular navigation for pages like /blog
              if (link.hash) {
                return (
                  <button
                    key={`${link.path}-${link.hash}`}
                    onClick={() => handleNavigation(link.path, link.hash)}
                    className="relative group transition-colors duration-300 focus-ring rounded-sm cursor-pointer bg-transparent border-none py-2 px-1 text-inherit after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 after:bg-[#1a1a1a] after:transition-all hover:after:w-full"
                    aria-label={link.ariaLabel}
                  >
                    <span className="relative z-10 font-medium group-hover:font-extrabold group-hover:tracking-[0.15em] group-hover:text-[#1a1a1a] transition-colors duration-200">
                      {link.label}
                    </span>
                    <span className="absolute inset-0 z-0 bg-primary/0 group-hover:bg-primary/20 rounded-lg blur-md opacity-0 group-hover:opacity-100 transition-all duration-500 pointer-events-none scale-50 group-hover:scale-150"></span>
                  </button>
                );
              }
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className="relative group transition-colors duration-300 focus-ring rounded-sm py-2 px-1 text-inherit after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 after:bg-[#1a1a1a] after:transition-all hover:after:w-full"
                  aria-label={link.ariaLabel}
                >
                  <span className="relative z-10 font-medium group-hover:font-extrabold group-hover:tracking-[0.15em] group-hover:text-[#1a1a1a] transition-colors duration-200">
                    {link.label}
                  </span>
                  <span className="absolute inset-0 z-0 bg-primary/0 group-hover:bg-primary/20 rounded-lg blur-md opacity-0 group-hover:opacity-100 transition-all duration-500 pointer-events-none scale-50 group-hover:scale-150"></span>
                </Link>
              );
            })}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button 
              variant="primary" 
              onClick={() => openContactForm('Book a Consultation')}
              className="px-6 py-2 pb-2.5 text-sm uppercase tracking-wider font-semibold hover:scale-[1.02] active:scale-[0.98] transition-transform"
            >
              Book a Consultation
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
            className="lg:hidden z-50 relative w-11 h-11 flex flex-col justify-center items-center focus-ring rounded-sm"
          >
            <span 
              className={`block w-6 h-0.5 bg-foreground transform transition duration-300 ease-in-out ${
                isMenuOpen ? 'rotate-45 translate-y-1.5' : ''
              }`}
              aria-hidden="true"
            />
            <span 
              className={`block w-6 h-0.5 bg-foreground transition duration-300 ease-in-out my-1 ${
                isMenuOpen ? 'opacity-0' : ''
              }`}
              aria-hidden="true"
            />
            <span 
              className={`block w-6 h-0.5 bg-foreground transform transition duration-300 ease-in-out ${
                isMenuOpen ? '-rotate-45 -translate-y-1.5' : ''
              }`}
              aria-hidden="true"
            />
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div 
        id="mobile-menu"
        ref={menuRef}
        className={`fixed inset-0 z-40 bg-background/95 backdrop-blur-md transform transition-transform duration-300 ease-in-out lg:hidden ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-hidden={!isMenuOpen}
        tabIndex={-1}
      >
        <div className="container mx-auto px-3 sm:px-4 lg:px-6 h-full flex flex-col justify-center items-center text-center">
          <nav 
            className="flex flex-col items-center space-y-8 text-2xl font-mono uppercase tracking-widest"
            aria-label="Mobile navigation"
          >
            {navLinks.map(link => (
              <button
                key={`mobile-${link.path}-${link.hash || 'index'}`}
                onClick={() => handleNavigation(link.path, link.hash)}
                className="transition-colors hover:text-primary focus-ring rounded-sm cursor-pointer bg-transparent border-none p-0 text-inherit"
                aria-label={link.ariaLabel}
              >
                {link.label}
              </button>
            ))}
          </nav>
          <div className="mt-12">
              <Button 
                variant="primary" 
                onClick={() => {
                  closeMenu();
                  openContactForm('Book a Consultation');
                }}
                className="w-full justify-center px-6 py-3 pb-3.5 text-sm uppercase tracking-wider font-semibold mt-4 shadow-sm"
              >
                Book a Consultation
              </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UnifiedHeader;
